import { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SuperAdminAuthContext } from '../context/SuperAdminAuthContext.jsx';
import './SuperAdminLayout.css';

/**
 * SuperAdmin Layout component with header, sidebar, and content area
 */
export default function SuperAdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { superAdmin, logout } = useContext(SuperAdminAuthContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/superadmin/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    closeSidebar();
  }, [pathname]);

  const menuItems = [
    { path: '/superadmin', label: 'Dashboard', icon: '📊' },
    { path: '/superadmin/organizations', label: 'Organizations', icon: '🏢' },
    { path: '/superadmin/companies', label: 'All Companies', icon: '🏗️' },
    { path: '/superadmin/users', label: 'All Users', icon: '👥' },
  ];

  const userDisplayName = superAdmin?.fullName || superAdmin?.username || 'Super Admin';

  return (
    <div className="superadmin-layout">
      {/* Header */}
      <header className="superadmin-layout-header">
        <button
          className="superadmin-layout-menu-toggle"
          onClick={toggleSidebar}
          aria-label="Toggle menu"
          aria-expanded={sidebarOpen}
        >
          <span className="superadmin-layout-menu-icon">☰</span>
        </button>

        <div className="superadmin-layout-logo">
          <span className="superadmin-layout-logo-text">Setu Link - Super Admin</span>
        </div>

        <div className="superadmin-layout-profile" ref={dropdownRef}>
          <button
            className="superadmin-layout-profile-chip"
            onClick={toggleDropdown}
            aria-label="User menu"
            aria-expanded={dropdownOpen}
          >
            <div className="superadmin-layout-profile-avatar">
              {userDisplayName.charAt(0).toUpperCase()}
            </div>
            <span className="superadmin-layout-profile-name">{userDisplayName}</span>
            <svg
              className="superadmin-layout-profile-arrow"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {dropdownOpen && (
            <div className="superadmin-layout-dropdown">
              <div className="superadmin-layout-dropdown-item">
                <span className="superadmin-layout-dropdown-label">Username:</span>
                <span className="superadmin-layout-dropdown-value">{superAdmin?.username}</span>
              </div>
              <div className="superadmin-layout-dropdown-item">
                <span className="superadmin-layout-dropdown-label">Email:</span>
                <span className="superadmin-layout-dropdown-value">{superAdmin?.email || 'N/A'}</span>
              </div>
              <div className="superadmin-layout-dropdown-divider"></div>
              <button
                className="superadmin-layout-dropdown-button"
                onClick={handleLogout}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 14H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h3M10 12l4-4-4-4M14 8H6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="superadmin-layout-body">
        {/* Sidebar */}
        <aside
          className={`superadmin-layout-sidebar ${sidebarOpen ? 'open' : ''}`}
          onClick={closeSidebar}
        >
          <nav className="superadmin-layout-nav">
            {menuItems.map((item) => {
              // For exact match: Dashboard should only match /superadmin
              // For others: match if pathname starts with the item path
              const isActive = item.path === '/superadmin' 
                ? pathname === item.path
                : pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`superadmin-layout-nav-item ${isActive ? 'active' : ''}`}
                >
                  <span className="superadmin-layout-nav-icon">{item.icon}</span>
                  <span className="superadmin-layout-nav-label">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="superadmin-layout-overlay"
            onClick={closeSidebar}
            aria-hidden="true"
          />
        )}

        {/* Main Content */}
        <main className="superadmin-layout-content">
          {children}
        </main>
      </div>
    </div>
  );
}

